import {
  addCreditCardMutation,
  getInvoiceBalanceByIdQuery,
  postPaymentMutation,
} from '../util/graphQL';
import { getMinimumpaymentAmount } from '../util/settings';
import { hasErrors, removeErrorKeys, validate } from '../util/validation';
import { history } from '../util/init';
import { navigateToReview } from './stepper-actions';
import actionTypes from '../constants/action-types';
import paymentAmountTypes from '../constants/payment-amount-type-constants';
import paymentTypes from '../constants/payment-type-constants';

const ccExpirationMonthError = 'Enter a 2 digit month';
const ccExpirationYearError = 'Enter a 2 digit year';

const newCardValidationRules = {
  cardNumber: {
    required: true,
    minLength: 13,
    maxLength: 16,
    creditCardNumber: true,
  },
  ccExpirationMonth: {
    required: true,
    requiredMessage: ccExpirationMonthError,
    maxLength: 2,
    maxLengthMessage: ccExpirationMonthError,
    maxValue: 12,
    maxValueMessage: ccExpirationMonthError,
    minLength: 2,
    minLengthMessage: ccExpirationMonthError,
    minValue: 1,
    minValueMessage: ccExpirationMonthError,
    number: true,
    numberMessage: ccExpirationMonthError,
  },
  ccExpirationYear: {
    required: true,
    requiredMessage: ccExpirationYearError,
    number: true,
    numberMessage: ccExpirationYearError,
    year: true,
  },
  cvn: {
    required: true,
    maxLength: 4,
    minLength: 3,
    number: true,
    minLengthMessage: 'Only 3-4 digits',
    maxLengthMessage: 'Only 3-4 digits',
  },
  firstName: {
    required: true,
    name: true,
    maxLength: 50,
  },
  lastName: {
    required: true,
    name: true,
    maxLength: 50,
  },
  billingAddress1: {
    required: true,
  },
  billingAddress2: {
    required: false,
  },
  billingCity: {
    required: true,
  },
  billingState: {
    required: true,
  },
  billingZipCode: {
    required: true,
  },
};

const existingCardValidationRules = {
  cvn: {
    required: true,
    maxLength: 4,
    minLength: 3,
    number: true,
    minLengthMessage: 'Only 3-4 digits',
    maxLengthMessage: 'Only 3-4 digits',
  },
};

export const updatePaymentMethod = (paymentType) => (dispatch) => {
  dispatch({ type: actionTypes.UPDATE_PAYMENT_TYPE, paymentType });
};

const getPaymentAmountValidationRule = (balanceDue) => {
  // Get minimum payment amount from configuration in environment variable
  // Allow the user to pay less than the minimum amount ONLY if the remaining balance is less than
  // the minimum payment
  const minimumPayment = Math.min(getMinimumpaymentAmount(), balanceDue);

  const balanceDisplay = balanceDue.toFixed(2);
  const minimumPaymentDisplay = minimumPayment.toFixed(2);
  return {
    paymentAmount: {
      required: true,
      currency: true,
      minValue: minimumPayment,
      minValueMessage: `Payment amount must be at least $${minimumPaymentDisplay}`,
      maxValue: balanceDue,
      maxValueMessage: `Payment amount must be less than or equal to $${balanceDisplay}`,
    },
  };
};

const getPaymentValidationRules = (
  paymentType,
  paymentAmountType,
  balanceDue
) => {
  // Existing cards only need to validate CVN
  const paymentMethodValidationRules =
    paymentType === paymentTypes.EXISTING
      ? existingCardValidationRules
      : newCardValidationRules;

  const amountValidation = getPaymentAmountValidationRule(balanceDue);

  // No need to validate "Full Amount"
  const amountValidationRules =
    paymentAmountType === paymentAmountTypes.PARTIAL ? amountValidation : {};

  // Combine the validation rules for method and amount
  return { ...paymentMethodValidationRules, ...amountValidationRules };
};

export const update = (changes) => (dispatch, getState) => {
  const {
    payment: { paymentType, paymentAmountType },
    invoice: { balanceDue },
  } = getState();

  // Combine the validation rules for method and amount
  const formValidationRules = getPaymentValidationRules(
    paymentType,
    paymentAmountType,
    balanceDue
  );

  const validatedForm = validate(changes, formValidationRules);
  dispatch({ type: actionTypes.UPDATE_PAYMENT_FORM, validatedForm });

  dispatch({ type: actionTypes.RESET_STEP_PAYMENT });
};

export const submit = () => (dispatch, getState) => {
  const { payment, invoice } = getState();
  const { invoiceId, accountId, paymentCode, creditCard, balanceDue } = invoice;

  const {
    billingAddress1,
    billingAddress2,
    billingState,
    billingCity,
    billingZipCode,
    firstName,
    lastName,
    cvn,
    ccExpirationMonth,
    ccExpirationYear,
    cardNumber,
    paymentType,
    paymentAmountType,
    saveCC,
  } = payment;

  const formValidationRules = getPaymentValidationRules(
    paymentType,
    paymentAmountType,
    balanceDue
  );

  // Remove any errors from review before validating payment
  const paymentForm = removeErrorKeys(payment);

  const validatedForm = validate(paymentForm, formValidationRules);

  // Take last 2 digits of card year. 22 -> 22 and 2022 -> 22
  const expYear = ccExpirationYear.slice(-2);

  // Change year to 21${expYear} in about 81 years
  const formattedExpirationDate = `${ccExpirationMonth}/20${expYear}`;

  const creditCardInput = {
    invoiceId,
    paymentCode,
    accountId,
    billingAddress1,
    billingAddress2,
    billingState,
    billingCity,
    billingZipCode,
    firstName,
    lastName,
    cvn,
    expirationDate: formattedExpirationDate,
    cardNumber,
    saveCC,
  };

  if (hasErrors(validatedForm)) {
    dispatch({ type: actionTypes.UPDATE_PAYMENT_FORM, validatedForm });
  } else if (paymentType === paymentTypes.EXISTING) {
    // Update payment state with cc info from the invoice
    dispatch({
      type: actionTypes.POST_CREDIT_CARD_SUCCESS,
      accountCreditId: creditCard.accountCreditId,
      ccDisplay: creditCard.ccDisplay,
    });
    dispatch(navigateToReview());
  } else {
    // New Card
    // call graphql mutation
    dispatch({ type: actionTypes.POST_CREDIT_CARD_REQUEST });
    return addCreditCardMutation(creditCardInput)
      .then((res) => {
        const { data, errors } = res;
        const { accountCreditId, ccDisplay } = data.addCard;
        if (accountCreditId === null && errors) {
          // For now, only get the first error
          const errorMessage = errors
            ? errors[0].Message
            : 'Error occurred while saving card.';
          dispatch({
            type: actionTypes.POST_CREDIT_CARD_ERROR,
            error: errorMessage,
          });
        }
        if (accountCreditId) {
          dispatch({
            type: actionTypes.POST_CREDIT_CARD_SUCCESS,
            accountCreditId,
            ccDisplay,
          });
          dispatch(navigateToReview());
        }
      })
      .catch((error) => {
        dispatch({
          type: actionTypes.POST_CREDIT_CARD_ERROR,
          error: error.message,
        });
      });
  }
  return null;
};

export const payInvoice = () => (dispatch, getState) => {
  const { payment, invoice } = getState();
  const { invoiceId, paymentCode } = invoice;

  const { paymentAmount, accountCreditId, cvn } = payment;

  const paymentInput = {
    invoiceId,
    paymentCode,
    amount: paymentAmount,
    paidByCC: true,
    accountCreditId,
    cvn,
  };

  if (accountCreditId === null) {
    delete paymentInput.accountCreditId;
  }

  dispatch({ type: actionTypes.POST_INVOICE_PAYMENT_REQUEST });
  return postPaymentMutation(paymentInput)
    .then((res) => {
      const { data, errors } = res;
      const { pay } = data;

      if (pay === null && errors) {
        // For now, only get the first error
        const errorMessage = errors ? errors[0].Message : 'An error occurred';
        dispatch({
          type: actionTypes.POST_INVOICE_PAYMENT_ERROR,
          error: errorMessage,
        });
      }

      if (pay && pay.invoicePaymentId) {
        const {
          invoicePaymentId,
          confirmationNumber,
          checkNumber,
          ccDisplay,
          referenceCode,
        } = data.pay;

        // Get the new updated balance from the server to display on the receipt
        return getInvoiceBalanceByIdQuery(invoiceId, paymentCode).then(
          (balanceRes) => {
            const { data: balanceData, errors: balanceErrors } = balanceRes;
            const balance = balanceData.getInvoice;
            if (balance === null) {
              const errorMessage = balanceErrors
                ? balanceErrors[0].Message
                : 'Invalid invoice number or payment code.';
              throw new Error(errorMessage);
            } else if (balance) {
              // Retrieved new balance successfully
              const { balanceDue: remainingBalance } = balance;

              // Now we can display the receipt
              return Promise.resolve(
                dispatch({
                  type: actionTypes.POST_INVOICE_PAYMENT_SUCCESS,
                  invoicePaymentId,
                  checkNumber,
                  ccDisplay,
                  referenceCode,
                  confirmationNumber,
                  remainingBalance,
                })
              ).then(() => history.push('/payment-success'));
            }
            return null;
          }
        );
      }
      return null;
    })
    .catch((error) => {
      dispatch({
        type: actionTypes.POST_INVOICE_PAYMENT_ERROR,
        error: error.message,
      });
    });
};

export const updatePaymentAmount = (paymentAmount) => (dispatch, getState) => {
  const { balanceDue } = getState().invoice;

  const amountValidation = getPaymentAmountValidationRule(
    Number(balanceDue.toFixed(2))
  );

  const validatedForm = validate({ paymentAmount }, amountValidation);

  return dispatch({ type: actionTypes.UPDATE_PAYMENT_AMOUNT, validatedForm });
};

export const setPaymentAmount = (paymentAmount) => (dispatch) => {
  dispatch({
    type: actionTypes.UPDATE_PAYMENT_AMOUNT,
    validatedForm: { paymentAmount },
  });
};

export const updatePaymentAmountType =
  (paymentAmountType) => (dispatch, getState) => {
    const { balanceDue } = getState().invoice;
    dispatch({
      type: actionTypes.UPDATE_PAYMENT_AMOUNT_TYPE,
      paymentAmountType,
    });

    // Set the payment amount to the full balance and re-run validation to clear any error
    dispatch(updatePaymentAmount(balanceDue.toFixed(2)));
  };
